// The registry: manifests in, the exact shapes the host application already reads
// out.
//
// This is the seam. Before it, adding a lens meant hand-editing four literals in
// three files plus a tab array — a set nothing named, which is why the surface was
// unbounded. After it, the host merges these projections into the literals it
// already has and every future lens is data.
//
// Deliberately ADDITIVE rather than a replacement. The host keeps its
// hand-written entries and merges manifest ones alongside, for two reasons: a
// lens can migrate to a manifest one at a time instead of in a single risky
// change, and a projection that collides with an existing key is a mistake worth
// reporting rather than silently winning.

import { validateXray } from "./manifest.js";

/**
 * @param {object[]} manifests  descriptors from `xrays/*.xray.js`
 * @param {object} opts
 *   reserved  keys the host already owns by hand. A manifest claiming one is an
 *             error: silently shadowing a working lens is the worst outcome here.
 */
export function createRegistry(manifests, { reserved = [] } = {}) {
  const problems = [];
  const lenses = [];
  const seen = new Set();
  const reservedSet = new Set(reserved);

  for (const m of manifests || []) {
    const v = validateXray(m);
    if (!v.ok) { problems.push(...v.problems.map((p) => `${m?.id || "<no id>"}: ${p}`)); continue; }
    if (seen.has(m.id)) { problems.push(`${m.id}: declared twice`); continue; }
    if (reservedSet.has(m.id)) {
      problems.push(`${m.id}: collides with a lens the host still wires by hand — rename, or migrate that one first`);
      continue;
    }
    seen.add(m.id);
    lenses.push(m);
  }

  return {
    problems,
    // A registry with problems still yields its VALID lenses. One bad manifest
    // must not blank the catalog — that would turn a typo into an outage.
    ok: problems.length === 0,
    lenses,

    /** For the host's substrate table: which tool names down-select each window. */
    substrates: () => Object.fromEntries(
      lenses.filter((m) => m.tab !== false)
        .map((m) => [m.id, { label: m.substrate.label, match: m.substrate.match }]),
    ),

    /** For the per-trace feed's domain labels. */
    feedDomains: () => Object.fromEntries(
      lenses.filter((m) => m.feed !== false).map((m) => [m.feed.domain, { label: m.label }]),
    ),

    /**
     * For the per-trace feed's extraction pass: one (domain, extract) pair each,
     * replacing a hand-written call per lens.
     */
    feedExtractors: () => lenses
      .filter((m) => m.feed !== false)
      .map((m) => ({ domain: m.feed.domain, extract: m.extract })),

    /** For the tab strip. `title` is the only place a reader learns what a tab covers. */
    tabs: () => lenses.filter((m) => m.tab !== false)
      .map((m) => ({ id: m.id, label: m.label, title: m.title, gate: m.gate || null })),

    /** For the scope panel: the window scan and grouping for one lens. */
    lens: (id) => lenses.find((m) => m.id === id) || null,

    /**
     * For the provenance graph: the lenses that project their ops into nodes.
     *
     * A lens that contributes nothing is simply absent — its reasoning lives in
     * its own `lineageReason`, which the contract requires. The host must not
     * keep a second list of which lenses count; that list was the thing this
     * projection replaced.
     */
    lineageSources: () => lenses.filter((m) => m.lineage).map((m) => ({ id: m.id, lineage: m.lineage })),

    /** For the audit stage: everything needed to re-run a lens over a corpus. */
    auditSpecs: () => lenses.map((m) => ({
      id: m.id, extract: m.extract, collect: m.collect,
      groupBy: m.groupBy || null, anchor: m.anchor || null, ignore: m.ignore || [],
    })),
  };
}

/**
 * Merge manifest projections over the host's hand-written literal.
 *
 * Reports collisions instead of resolving them. A manifest quietly overwriting a
 * working lens' substrate is a defect that presents as "the tab scans the wrong
 * window", which is exactly the class of silent wrongness this whole effort keeps
 * running into.
 */
export function mergeInto(existing, projected) {
  const collisions = Object.keys(projected).filter((k) => k in (existing || {}));
  return {
    merged: { ...(existing || {}), ...projected },
    collisions,
    ok: collisions.length === 0,
  };
}
